/**
 * EntityForm React context.
 *
 * Provides the EntityFormConfig and UseEntityFormReturn state to all
 * compound sub-components (Header, Section, Field, Fields, Actions, Alert).
 *
 * Follows the same context pattern used by Modal and Card in the base
 * component library: a nullable context with a guard hook that throws
 * when used outside the provider.
 */

import { createContext, useContext } from 'react';
import type { EntityFormConfig, UseEntityFormReturn } from '../types';

// ---------------------------------------------------------------------------
// Context Value
// ---------------------------------------------------------------------------

/**
 * Value provided by the EntityForm root component to all descendants.
 */
export interface EntityFormContextValue {
  /** The resolved form configuration */
  config: EntityFormConfig;

  /** Form state and actions from useEntityForm */
  form: UseEntityFormReturn;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/**
 * React context for EntityForm compound components.
 * Initialised as `null` -- the provider is set up in EntityFormRoot.
 */
export const EntityFormContext =
  createContext<EntityFormContextValue | null>(null);

EntityFormContext.displayName = 'EntityFormContext';

// ---------------------------------------------------------------------------
// Consumer Hook
// ---------------------------------------------------------------------------

/**
 * Access the EntityForm context from any compound sub-component.
 *
 * @throws {Error} When called outside of an EntityForm provider.
 *
 * @example
 * ```tsx
 * function EntityFormField({ name }: { name: string }) {
 *   const { config, form } = useEntityFormContext();
 *   const fieldConfig = config.fields[name];
 *   // ...
 * }
 * ```
 */
export function useEntityFormContext(): EntityFormContextValue {
  const context = useContext(EntityFormContext);
  if (!context) {
    throw new Error(
      'EntityForm compound components must be used within <EntityForm>',
    );
  }
  return context;
}
